import React, { useEffect, useMemo, useState, useCallback } from "react";
import natureBg from "./img/Foto2wilmanatur.jpg";
import "./css/inventory.css";
import { useNavigate } from "react-router-dom";
import {getInventoryItems,downloadInventoryCsv,createInventoryItem,updateInventoryItem,deleteInventoryItem,} from "./api/inventoryApi";
import { useLang } from "./hooks/useLang";
import { DICT } from "./i18n";

function Inventory() {
  const navigate = useNavigate();
  const [lang] = useLang();
  const t = DICT[lang] ?? DICT.sv;
  const [location, setLocation] = useState("karstugan");
  const [itemsByYear, setItemsByYear] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newQty, setNewQty] = useState(1);
  const [newCondition, setNewCondition] = useState("");
  const currentYear = new Date().getFullYear();
  const YEAR_OPTIONS = Array.from(
    { length: currentYear - 2010 + 1 },
    (_, i) => String(2010 + i)
  ).reverse();
  const [yearsToCompare, setYearsToCompare] = useState(() => {
    const y = new Date().getFullYear();
    return [String(y), String(y - 1), String(y - 2)];
  });
  const [newLocation, setNewLocation] = useState(location);
  const [newYear, setNewYear] = useState(yearsToCompare[0]);
  const CATEGORY_KEYS = useMemo(
    () => ["equipment", "kitchen", "tools", "tents", "other"],
    []
  );
  const CATEGORY_LABELS = useMemo(
    () => ({
      equipment: t.cat_equipment,
      kitchen: t.cat_kitchen,
      tools: t.cat_tools,
      tents: t.cat_tents,
      other: t.cat_other,
    }),
    [t]
  );
  const svToKey = useMemo(
    () => ({
      Utrustning: "equipment",
      Kök: "kitchen",
      Verktyg: "tools",
      Tält: "tents",
      Övrigt: "other",
    }),
    []
  );
  const keyToSv = useMemo(
    () => ({
      equipment: "Utrustning",
      kitchen: "Kök",
      tools: "Verktyg",
      tents: "Tält",
      other: "Övrigt",
    }),
    []
  );
  const [editCell, setEditCell] = useState(null);
  const allItems = useMemo(() => Object.values(itemsByYear).flat(), [itemsByYear]);
  const getItemForCell = (catKey, name, year) => {
    const arr = itemsByYear[year] || [];
    return (
      arr.find((x) => {
        const stored = x.category || "Övrigt";
        const storedKey = svToKey[stored] || "other";
        return storedKey === catKey && x.name === name;
      }) || null
    );
  };
  const openCellPopup = (catKey, name, year) => {
    const item = getItemForCell(catKey, name, year);
    if (!item) return;
    setEditCell({ catKey, name, year, item });
  };
  const closeCellPopup = () => setEditCell(null);

  const refresh = useCallback(async () => {
  if (!yearsToCompare.length) return;

  setLoading(true);
  setError("");

  try {
    const results = await Promise.all(
      yearsToCompare.map(async (y) => {
        const data = await getInventoryItems({ location, year: y });
        return [y, data];
      })
    );

    setItemsByYear(Object.fromEntries(results));
  } catch (e) {
    setError(e?.message || t.invFetchError);
  } finally {
    setLoading(false);
  }
}, [yearsToCompare, location, t.invFetchError]);


useEffect(() => {
  refresh();
}, [refresh]);

  const namesByCategoryKey = useMemo(() => {
    const map = {};
    for (const k of CATEGORY_KEYS) map[k] = new Set();

    for (const it of allItems) {
      const stored = it.category || "Övrigt";
      const key = svToKey[stored] || "other";
      if (!map[key]) map[key] = new Set();
      map[key].add(it.name);
    }
    const out = {};
    for (const [k, set] of Object.entries(map)) {
      out[k] = Array.from(set).sort((a, b) => a.localeCompare(b));
    }
    return out;
  }, [allItems, CATEGORY_KEYS, svToKey]);
  const getQty = (catKey, name, year) => {
    const arr = itemsByYear[year] || [];
    const found = arr.find((x) => {
      const stored = x.category || "Övrigt";
      const storedKey = svToKey[stored] || "other";
      return storedKey === catKey && x.name === name;
    });
    return found ? Number(found.qty) || 0 : "";
  };
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;

    if (!newCategory) {
      setError(t.chooseCategoryErr);
      return;
    }

    try {
      const categorySv = keyToSv[newCategory] || "Övrigt";

      await createInventoryItem({
        name: newName.trim(),
        category: categorySv,
        qty: Number(newQty) || 0,
        condition: newCondition.trim(),
        location: newLocation,
        year: Number(newYear),
      });

      setShowAdd(false);
      setNewName("");
      setNewCategory("");
      setNewQty(1);
      setNewCondition("");

      await refresh();
    } catch (e2) {
      setError(e2?.message || t.couldNotAddItem);
    }
  };

  return (
    <div className="page">
      <div className="page-bg" style={{ backgroundImage: `url(${natureBg})` }} />
      <div className="page-content inv-file-page">
        <header className="inv-file-header" onClick={() => navigate("/homelogin")}>
          Scoutkåren Vasa Korsfarare
        </header>

        <aside className="inv-sidebar">
          <img
            src={require("./img/treetop.png")}
            alt="tree top"
            className="inv-sidebar-tree"
          />
          <div className="inv-sidebar-logo-wrapper">
            <img
              src={require("./img/svk.png")}
              alt="logo"
              className="inv-sidebar-logo"
            />
            <div className="inv-sidebar-logo-bubble">{t.welcome}</div>
          </div>

          <button className="inv-menu-btn" onClick={() => navigate("/file")}>
            {t.files}
          </button>
          <button className="inv-menu-btn" onClick={() => navigate("/todo")}>
            {t.todo}
          </button>
          <button className="inv-menu-btn" onClick={() => navigate("/inventory")}>
            {t.inventory}
          </button>
          <button className="inv-menu-btn" onClick={() => navigate("/homelogin")}>
            {t.back}
          </button>
        </aside>

        <main className="inventory-content">
          <h2 className="inventory-title">{t.invTitle}</h2>

          <div className="location-switch">
            <button
              className={location === "karstugan" ? "location-btn active" : "location-btn"}
              onClick={() => setLocation("karstugan")}
            >
              {t.scoutHq}
            </button>

            <button
              className={location === "forrad" ? "location-btn active" : "location-btn"}
              onClick={() => setLocation("forrad")}
            >
              {t.cabin}
            </button>
          </div>

          <div className="inventory-toolbar">
            <div className="inventory-years-compare">
              <label>{t.compareYears}</label>

              {yearsToCompare.map((y, idx) => (
                <select
                  key={idx}
                  value={y}
                  onChange={(e) => {
                    const next = [...yearsToCompare];
                    next[idx] = e.target.value;
                    setYearsToCompare(next);
                  }}
                >
                  {YEAR_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ))}
            </div>

            <button
              className="inventory-download-btn"
              type="button"
              onClick={() => downloadInventoryCsv({ location, year: yearsToCompare[0] })}
            >
              {t.downloadCsv}
            </button>

            <button
              className="inventory-add-btn"
              type="button"
              onClick={() => {
                setNewLocation(location);
                setNewYear(yearsToCompare[0]);
                setShowAdd(true);
              }}
              disabled={!yearsToCompare[0]}
            >
              {t.addItem}
            </button>
          </div>

          {error && <div className="inventory-error">{error}</div>}
          {loading && <div className="inventory-loading">{t.loadingInv}</div>}

          <div className="inventory-card">
            {CATEGORY_KEYS.map((catKey) => {
              const names = namesByCategoryKey[catKey] || [];
              const catLabel = CATEGORY_LABELS[catKey] || catKey;

              return (
                <div key={catKey} className="inv-category-block">
                  <h3 className="inv-category-title">{catLabel}</h3>

                  {names.length === 0 ? (
                    <div className="inv-empty-category">{t.emptyCategory}</div>
                  ) : (
                    <table className="inv-compare-table">
                      <thead>
                        <tr>
                          <th>{t.things}</th>
                          {yearsToCompare.map((y) => (
                            <th key={y}>{y}</th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {names.map((name) => (
                          <tr key={name}>
                            <td className="inv-name">{name}</td>
                            {yearsToCompare.map((y) => (
                              <td key={y} className="inv-qty-cell">
                                {(() => {
                                  const qty = getQty(catKey, name, y);
                                  if (qty === "") return "";
                                  return (
                                    <button
                                      type="button"
                                      className="inv-qty-btn"
                                      onClick={() => openCellPopup(catKey, name, y)}
                                      title={t.clickToEdit}
                                    >
                                      {qty}
                                    </button>
                                  );
                                })()}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>

          {showAdd && (
            <div className="inv-modal-overlay" onClick={() => setShowAdd(false)}>
              <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="inv-modal-title">{t.addItemTitle}</h3>

                <form className="inv-modal-form" onSubmit={handleCreate}>
                  <label>
                    {t.things}
                    <input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={t.thingPlaceholder}
                      autoFocus
                    />
                  </label>

                  <label>
                    {t.category}
                    <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
                      <option value="">{t.chooseCategory}</option>
                      {CATEGORY_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {CATEGORY_LABELS[k]}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    {t.qty}
                    <input
                      type="number"
                      min="0"
                      value={newQty}
                      onChange={(e) => setNewQty(e.target.value)}
                    />
                  </label>

                  <label>
                    {t.status}
                    <input
                      value={newCondition}
                      onChange={(e) => setNewCondition(e.target.value)}
                      placeholder={t.statusPlaceholder}
                    />
                  </label>

                  <label>
                    {t.place}
                    <select value={newLocation} onChange={(e) => setNewLocation(e.target.value)}>
                      <option value="karstugan">{t.scoutHq}</option>
                      <option value="forrad">{t.cabin}</option>
                    </select>
                  </label>

                  <label>
                    {t.year}
                    <select value={newYear} onChange={(e) => setNewYear(e.target.value)}>
                      {YEAR_OPTIONS.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="inv-modal-meta">
                    <span>
                      {t.placeNow}: <strong>{newLocation === "karstugan" ? t.scoutHq : t.cabin}</strong>
                    </span>
                    <span>
                      {t.yearNow}: <strong>{newYear}</strong>
                    </span>
                  </div>

                  <div className="inv-modal-actions">
                    <button className="inv-modal-save" type="submit">
                      {t.save}
                    </button>
                    <button
                      className="inv-modal-cancel"
                      type="button"
                      onClick={() => setShowAdd(false)}
                    >
                      {t.cancel}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {editCell && (
            <div className="inv-modal-overlay" onClick={closeCellPopup}>
              <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="inv-modal-title">
                  {editCell.name} ({editCell.year})
                </h3>

                <div className="inv-modal-meta">
                  <span>
                    {t.catLabel}:{" "}
                    <strong>{CATEGORY_LABELS[editCell.catKey] || editCell.catKey}</strong>
                  </span>
                  <span>
                    {t.placeLabel}:{" "}
                    <strong>{location === "karstugan" ? t.scoutHq : t.cabin}</strong>
                  </span>
                  <span>
                    {t.qtyNow}: <strong>{editCell.item.qty}</strong>
                  </span>
                </div>

                {error && <div className="inventory-error">{error}</div>}

                <div className="inv-modal-actions" style={{ gap: "10px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="inv-modal-save"
                    onClick={async () => {
                      try {
                        const updated = await updateInventoryItem(editCell.item.id, {
                          qty: Number(editCell.item.qty) + 1,
                        });
                        await refresh();
                        setEditCell((prev) => (prev ? { ...prev, item: updated } : prev));
                      } catch (e) {
                        setError(e?.message || t.couldNotUpdate);
                      }
                    }}
                  >
                    +1
                  </button>

                  <button
                    type="button"
                    className="inv-modal-save"
                    onClick={async () => {
                      try {
                        const nextQty = Number(editCell.item.qty) - 1;

                        if (nextQty <= 0) {
                          if (!window.confirm(t.confirmRowDelete)) return;
                          await deleteInventoryItem(editCell.item.id);
                          closeCellPopup();
                          await refresh();
                          return;
                        }

                        const updated = await updateInventoryItem(editCell.item.id, { qty: nextQty });
                        await refresh();
                        setEditCell((prev) => (prev ? { ...prev, item: updated } : prev));
                      } catch (e) {
                        setError(e?.message || t.couldNotUpdate);
                      }
                    }}
                  >
                    -1
                  </button>

                  <button
                    type="button"
                    className="todo-delete-btn"
                    onClick={async () => {
                      try {
                        const msg =
                          typeof t.confirmDeleteYear === "function"
                            ? t.confirmDeleteYear(editCell.name, editCell.year)
                            : `Delete "${editCell.name}" for year ${editCell.year}?`;

                        if (!window.confirm(msg)) return;

                        await deleteInventoryItem(editCell.item.id);
                        closeCellPopup();
                        await refresh();
                      } catch (e) {
                        setError(e?.message || t.couldNotDelete);
                      }
                    }}
                  >
                    {t.delete}
                  </button>

                  <button type="button" className="inv-modal-cancel" onClick={closeCellPopup}>
                    {t.close}
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default Inventory;
